import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { ApiError } from '../infrastructure/api/BaseApiClient';
import type { KvsBusbar, KvsBusbarSwitch, Kabelverteilschrank } from '../types/network';

/** Input for creating/updating a busbar */
export interface BusbarInput {
  name: string;
  inService?: boolean;
  maxCurrentA?: number;
  description?: string;
}

/** Input for creating a busbar switch */
export interface BusbarSwitchInput {
  busbarAId: string;
  busbarBId: string;
  name?: string;
  isClosed?: boolean;
  switchType?: string;
}

/** Standard API response */
interface ApiResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/** Busbar API response */
interface BusbarResponse extends ApiResponse {
  busbar?: KvsBusbar;
}

/** Busbar switch API response */
interface BusbarSwitchResponse extends ApiResponse {
  busbarSwitch?: KvsBusbarSwitch;
}

/** Cable assignment API response */
interface AssignCableResponse extends ApiResponse {
  kvs?: Kabelverteilschrank;
}

/**
 * Service for managing kabelverteilschrank operations
 */
export const kvsService = {
  /**
   * Deletes a kabelverteilschrank and clears all references to it
   * @param kvsId The ID of the kabelverteilschrank to delete
   * @returns Promise with the result of the operation
   */
  async deleteKabelverteilschrank(kvsId: string): Promise<ApiResponse> {
    try {
      const response = await networkApi.zoneDelete<{ message: string }>(`/network/kvs/${kvsId}`);
      return {
        success: true,
        message: response.message
      };
    } catch (error) {
      console.error('[KvsService] Failed to delete kabelverteilschrank:', error);
      if (error instanceof ApiError) {
        return {
          success: false,
          error: error.message
        };
      }
      return {
        success: false,
        error: 'An unknown error occurred while deleting the kabelverteilschrank'
      };
    }
  },

  // ============================================
  // Busbar CRUD Operations
  // ============================================

  /**
   * Add a new busbar to a KVS
   */
  async addBusbar(kvsId: string, busbar: BusbarInput): Promise<BusbarResponse> {
    try {
      const response = await networkApi.zonePost<BusbarResponse>(
        `/network/kvs/${kvsId}/busbars`,
        busbar
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to add busbar:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to add busbar' };
    }
  },

  /**
   * Update an existing busbar
   */
  async updateBusbar(kvsId: string, busbarId: string, busbar: BusbarInput): Promise<BusbarResponse> {
    try {
      const response = await networkApi.zonePatch<BusbarResponse>(
        `/network/kvs/${kvsId}/busbars/${busbarId}`,
        busbar
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to update busbar:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to update busbar' };
    }
  },

  /**
   * Delete a busbar from a KVS
   */
  async deleteBusbar(kvsId: string, busbarId: string): Promise<ApiResponse> {
    try {
      const response = await networkApi.zoneDelete<ApiResponse>(
        `/network/kvs/${kvsId}/busbars/${busbarId}`
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to delete busbar:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to delete busbar' };
    }
  },

  // ============================================
  // Busbar Switch Operations
  // ============================================

  /**
   * Add a coupling switch between two busbars
   */
  async addBusbarSwitch(kvsId: string, sw: BusbarSwitchInput): Promise<BusbarSwitchResponse> {
    try {
      const response = await networkApi.zonePost<BusbarSwitchResponse>(
        `/network/kvs/${kvsId}/busbar-switches`,
        sw
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to add busbar switch:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to add busbar switch' };
    }
  },

  /**
   * Toggle a busbar switch open/closed state
   */
  async toggleBusbarSwitch(kvsId: string, switchId: string, isClosed: boolean): Promise<BusbarSwitchResponse> {
    try {
      const response = await networkApi.zonePatch<BusbarSwitchResponse>(
        `/network/kvs/${kvsId}/busbar-switches/${switchId}`,
        isClosed
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to toggle busbar switch:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to toggle busbar switch' };
    }
  },

  /**
   * Delete a busbar switch
   */
  async deleteBusbarSwitch(kvsId: string, switchId: string): Promise<ApiResponse> {
    try {
      const response = await networkApi.zoneDelete<ApiResponse>(
        `/network/kvs/${kvsId}/busbar-switches/${switchId}`
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to delete busbar switch:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to delete busbar switch' };
    }
  },

  // ============================================
  // Cable Assignment Operations
  // ============================================

  /**
   * Update fuse type, current, and inserted state for a cable connection
   */
  async updateFuse(kvsId: string, cableId: string, fuse: { type: string; current: number; isInserted: boolean }): Promise<ApiResponse> {
    try {
      const response = await networkApi.zonePut<ApiResponse>(
        `/network/kvs/${kvsId}/fuses/${cableId}`,
        fuse
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to update fuse:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to update fuse' };
    }
  },

  /**
   * Toggle fuse inserted state for a cable connection
   */
  async toggleFuseInserted(kvsId: string, cableId: string, isInserted: boolean): Promise<ApiResponse> {
    try {
      const response = await networkApi.zonePatch<ApiResponse>(
        `/network/kvs/${kvsId}/fuses/${cableId}/toggle-inserted`,
        { isInserted }
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to toggle fuse inserted state:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to toggle fuse inserted state' };
    }
  },

  /**
   * Assign a cable to a specific busbar
   */
  async assignCableToBusbar(kvsId: string, busbarId: string, cableId: string): Promise<AssignCableResponse> {
    try {
      const response = await networkApi.zonePost<AssignCableResponse>(
        `/network/kvs/${kvsId}/busbars/${busbarId}/assign-cable`,
        { cableId }
      );
      return response;
    } catch (error) {
      console.error('[KvsService] Failed to assign cable to busbar:', error);
      if (error instanceof ApiError) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to assign cable to busbar' };
    }
  }
};

export default kvsService;
